/**
 * The figures, grouped the way a credit file is read, one column per fiscal year.
 *
 * Every row with evidence is a button: clicking it opens the page it was read from, with the
 * quoted sentence highlighted. That is the trust mechanism of the whole product, so it costs one
 * click and nothing else.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import {
  conflictRows,
  figureSections,
  normalisationNote,
  ratioRows,
  validationRows,
  verificationIndex,
  verificationKey,
  verificationProgress,
} from "../../lib/model";
import type { EvidenceRequest } from "../../components/EvidenceDrawer";
import {
  Chip,
  Dot,
  EmptyState,
  ErrorBanner,
  SectionTitle,
  Spinner,
  Table,
  Td,
  Th,
} from "../../components/ui";

export default function Figures({
  caseId,
  onEvidence,
}: {
  caseId: string;
  onEvidence: (request: EvidenceRequest) => void;
}) {
  const profile = useQuery({
    queryKey: ["profile", caseId],
    queryFn: () => api.getProfile(caseId),
    retry: false,
  });
  const verifications = useQuery({
    queryKey: ["verifications", caseId],
    queryFn: () => api.listVerifications(caseId),
  });

  if (profile.isLoading) return <Spinner />;
  if (!profile.data) {
    return (
      <EmptyState
        title="Todavía no hay cifras consolidadas"
        text="Aparecen cuando el análisis termina de leer los documentos."
      />
    );
  }

  const data = profile.data;
  const sections = figureSections(data);
  const ratios = ratioRows(data);
  const conflicts = conflictRows(data);
  const validations = validationRows(data);
  const failed = validations.filter((v) => !v.passed);
  const checked = verificationIndex(verifications.data ?? []);
  const progress = verificationProgress(sections, checked);

  return (
    <>
      <p className="text-sm text-muted">
        Versión {data.version} del perfil · cierre de ejercicio{" "}
        {data.fiscal_year_end ?? "sin dato"} · moneda {data.currency ?? "ARS"}
      </p>

      {conflicts.length ? (
        <div className="mt-4">
          <ErrorBanner
            message={`${conflicts.length} concepto(s) con valores distintos entre documentos. Se detallan abajo con el criterio que se aplicó.`}
          />
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          Hacé clic en una fila para ver de qué documento y página salió cada cifra, y marcarla
          como verificada.
        </p>
        {progress.total ? (
          <p className="text-sm">
            <span className="font-medium">
              {progress.checked} de {progress.total}
            </span>{" "}
            <span className="text-muted">cifras verificadas</span>
            {progress.disagreed ? (
              <span className="ml-2 font-medium text-bad">
                · {progress.disagreed} con diferencias
              </span>
            ) : null}
          </p>
        ) : null}
      </div>

      {sections.map((section) => (
        <div key={section.name}>
          <SectionTitle>{section.name}</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Concepto</Th>
                {section.periods.map((period) => (
                  <Th key={period} right>
                    {period}
                  </Th>
                ))}
                <Th>Confianza</Th>
                <Th>Revisar</Th>
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row) => {
                const clickable = !!row.evidenceId;
                const period = section.periods[0] === "Valor" ? null : (section.periods[0] ?? null);
                const state = checked.get(verificationKey(row.name, period));
                const open = () =>
                  clickable &&
                  onEvidence({
                    evidenceId: row.evidenceId!,
                    title: row.label,
                    note: normalisationNote(data, row.name, period),
                    caseId,
                    fieldName: row.name,
                    period,
                  });
                return (
                  <tr
                    key={row.name}
                    tabIndex={clickable ? 0 : -1}
                    onClick={open}
                    onKeyDown={(e) => {
                      if (clickable && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        open();
                      }
                    }}
                    className={clickable ? "cursor-pointer hover:bg-canvas" : ""}
                  >
                    <Td className="font-medium">{row.label}</Td>
                    {section.periods.map((period) => (
                      <Td key={period} right>
                        {row.values[period] ?? "—"}
                      </Td>
                    ))}
                    <Td>
                      <span className="flex items-center gap-2 text-muted">
                        <Dot tone={row.confidence.tone} />
                        {row.confidence.text}
                      </span>
                    </Td>
                    <Td>
                      {row.conflict ? <Chip text="Conflicto" tone="warn" /> : null}
                      {state?.checked ? (
                        <Chip
                          text={state.agrees ? "Verificada" : "No coincide"}
                          tone={state.agrees ? "good" : "bad"}
                        />
                      ) : null}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ))}

      {ratios.rows.length ? (
        <>
          <SectionTitle hint="Calculados por el sistema a partir de las cifras extraídas, no tomados de los documentos. Las variaciones entre ejercicios son reales: el cierre anterior se expresa en moneda del último, con el índice de precios del BCRA. Un guion en la variación real quiere decir que no hubo índice para hacerlo — no que no haya variación.">
            Indicadores calculados
          </SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Indicador</Th>
                {ratios.periods.map((period) => (
                  <Th key={period} right>
                    {period}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ratios.rows.map((row) => (
                <tr key={row.label}>
                  <Td className="font-medium">{row.label}</Td>
                  {ratios.periods.map((period) => (
                    <Td key={period} right>
                      {row.values[period] ?? "—"}
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : null}

      {conflicts.length ? (
        <>
          <SectionTitle>Diferencias entre documentos</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Concepto</Th>
                <Th>Ejercicio</Th>
                <Th>Valor adoptado</Th>
                <Th>Cómo se resolvió</Th>
              </tr>
            </thead>
            <tbody>
              {conflicts.map((row, index) => (
                <tr key={index}>
                  <Td className="font-medium">{row.label}</Td>
                  <Td>{row.period}</Td>
                  <Td>{row.value}</Td>
                  <Td className="text-muted">{row.resolution}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : null}

      {validations.length ? (
        <>
          <SectionTitle hint={`${failed.length} de ${validations.length} controles con observaciones.`}>
            Controles de consistencia
          </SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Resultado</Th>
                <Th>Severidad</Th>
                <Th>Control</Th>
                <Th>Campos</Th>
              </tr>
            </thead>
            <tbody>
              {validations.map((row, index) => (
                <tr key={index}>
                  <Td>
                    <span className="flex items-center gap-2">
                      <Dot tone={row.passed ? "good" : row.tone} />
                      {row.passed ? "Cumple" : "No cumple"}
                    </span>
                  </Td>
                  <Td className="text-muted">{row.severity}</Td>
                  <Td>{row.message}</Td>
                  <Td className="text-muted">{row.fields}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : null}
    </>
  );
}
